import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FaArrowLeft,
    FaSearch,
    FaSyncAlt,
    FaToggleOn,
    FaToggleOff,
    FaKey,
    FaCopy,
    FaUsers,
    FaMicrochip,
    FaTimes,
    FaEdit,
    FaSave
} from 'react-icons/fa';

import { request } from '../../services/api';
import { CircuitBackgroundLayer } from '../../components/CircuitBackground/AnimatedCircuitChipBackground';
import styles from '../Monitor/Monitor.module.css';

const AdminUsers = () => {
    const navigate = useNavigate();

    const [search, setSearch] = useState('');
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [error, setError] = useState('');
    const [resetResult, setResetResult] = useState(null);

    const [editUser, setEditUser] = useState(null);
    const [editForm, setEditForm] = useState({
        username: '',
        name: '',
        family: '',
        phone_num1: '',
        phone_num2: ''
    });
    const [editLoading, setEditLoading] = useState(false);

    const loadUsers = async (searchText = search) => {
        setLoading(true);
        setError('');

        try {
            const json = await request(
                `/admin_get_users.php?search=${encodeURIComponent(searchText.trim())}&limit=50`
            );

            if (!json || json.status !== 'success') {
                throw new Error(json?.message || 'خطا در دریافت کاربران');
            }

            setUsers(json.users || []);
        } catch (err) {
            console.error('Admin users load error:', err);
            setError(err.message || 'خطا در دریافت کاربران');
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            loadUsers(search);
        }, 350);

        return () => clearTimeout(timer);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const toggleUser = async (user) => {
        const nextStatus = Number(user.is_active) === 1 ? 0 : 1;

        const confirmText = nextStatus === 1
            ? `کاربر ${user.username} فعال شود؟`
            : `کاربر ${user.username} غیرفعال شود؟`;

        if (!window.confirm(confirmText)) return;

        setActionLoadingId(user.id);
        setError('');

        try {
            const json = await request('/admin_toggle_user.php', 'POST', {
                user_id: user.id,
                is_active: nextStatus
            });

            if (!json || json.status !== 'success') {
                throw new Error(json?.message || 'خطا در تغییر وضعیت کاربر');
            }

            setUsers(prev =>
                prev.map(item =>
                    item.id === user.id
                        ? { ...item, is_active: nextStatus }
                        : item
                )
            );
        } catch (err) {
            console.error('Toggle user error:', err);
            setError(err.message || 'خطا در تغییر وضعیت کاربر');
        } finally {
            setActionLoadingId(null);
        }
    };

    const resetPassword = async (user) => {
        if (!window.confirm(`رمز عبور کاربر ${user.username} ریست شود؟`)) return;

        setActionLoadingId(user.id);
        setError('');
        setResetResult(null);

        try {
            const json = await request('/admin_reset_user_password.php', 'POST', {
                user_id: user.id
            });

            if (!json || json.status !== 'success') {
                throw new Error(json?.message || 'خطا در ریست رمز عبور');
            }

            setResetResult(json);
        } catch (err) {
            console.error('Reset password error:', err);
            setError(err.message || 'خطا در ریست رمز عبور');
        } finally {
            setActionLoadingId(null);
        }
    };

    const openEditModal = (user) => {
        setEditUser(user);
        setEditForm({
            username: user.username || '',
            name: user.name || '',
            family: user.family || '',
            phone_num1: user.phone_num1 || '',
            phone_num2: user.phone_num2 || ''
        });
        setError('');
    };

    const closeEditModal = () => {
        setEditUser(null);
        setEditForm({
            username: '',
            name: '',
            family: '',
            phone_num1: '',
            phone_num2: ''
        });
        setEditLoading(false);
    };

    const submitEditUser = async (e) => {
        e.preventDefault();

        if (!editUser) return;

        if (!editForm.username.trim()) {
            setError('نام کاربری الزامی است');
            return;
        }

        if (!editForm.phone_num1.trim()) {
            setError('شماره موبایل اصلی الزامی است');
            return;
        }

        setEditLoading(true);
        setError('');

        try {
            const json = await request('/admin_update_user.php', 'POST', {
                user_id: editUser.id,
                username: editForm.username.trim(),
                name: editForm.name.trim(),
                family: editForm.family.trim(),
                phone_num1: editForm.phone_num1.trim(),
                phone_num2: editForm.phone_num2.trim()
            });

            if (!json || json.status !== 'success') {
                throw new Error(json?.message || 'خطا در ویرایش کاربر');
            }

            setUsers(prev =>
                prev.map(item =>
                    item.id === editUser.id
                        ? { ...item, ...json.user }
                        : item
                )
            );

            closeEditModal();
        } catch (err) {
            console.error('Update user error:', err);
            setError(err.message || 'خطا در ویرایش کاربر');
        } finally {
            setEditLoading(false);
        }
    };

    const copyText = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            alert('کپی شد');
        } catch {
            alert('امکان کپی وجود ندارد');
        }
    };

    const buildPasswordMessage = () => {
        if (!resetResult) return '';

        return [
            'رمز عبور جدید پنل هوشمند گلخانه:',
            `نام کاربری: ${resetResult.user.username}`,
            `رمز عبور جدید: ${resetResult.plain_password}`,
            'آدرس پنل: https://sfarm.tabangohar.com'
        ].join('\n');
    };

    const clearSearch = () => {
        setSearch('');
    };

    return (
        <div className={styles.container}>
            <CircuitBackgroundLayer />
            <div className={styles.overlay}></div>

            <div className={styles.content}>
                <div className={styles.header}>
                    <div className={styles.deviceTitle}>
                        <span style={{ fontSize: '2rem' }}>👥</span>
                        <div>
                            <div style={{ fontWeight: 'bold' }}>مدیریت کاربران</div>
                            <span style={{ fontSize: '12px', color: '#ccc' }}>
                                جستجو، ویرایش، فعال‌سازی، غیرفعال‌سازی و ریست رمز مشتریان
                            </span>
                        </div>
                    </div>

                    <button
                        onClick={() => navigate('/admin')}
                        className={styles.backBtn}
                        type="button"
                    >
                        <FaArrowLeft /> بازگشت به مدیریت
                    </button>
                </div>

                <div style={topBarStyle}>
                    <div style={searchWrapperStyle}>
                        <FaSearch style={searchIconInlineStyle} />

                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="جستجو با نام، نام خانوادگی، موبایل یا نام کاربری..."
                            style={searchInputStyle}
                        />

                        {search && (
                            <button
                                type="button"
                                onClick={clearSearch}
                                style={clearSearchButtonStyle}
                                title="پاک کردن"
                            >
                                <FaTimes />
                            </button>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={() => loadUsers(search)}
                        style={refreshButtonStyle}
                        disabled={loading}
                    >
                        <FaSyncAlt />
                        {loading ? 'در حال دریافت...' : 'بروزرسانی'}
                    </button>
                </div>

                {error && (
                    <div style={errorStyle}>
                        {error}
                    </div>
                )}

                {resetResult && (
                    <div style={resetResultStyle}>
                        <div style={resetHeaderStyle}>
                            <strong>رمز عبور جدید ساخته شد</strong>

                            <button
                                type="button"
                                onClick={() => setResetResult(null)}
                                style={smallCloseButtonStyle}
                                title="بستن"
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div style={resetInfoGridStyle}>
                            <InfoRow label="نام کاربری" value={resetResult.user.username} />
                            <InfoRow label="رمز جدید" value={resetResult.plain_password} />
                            <InfoRow
                                label="نام کاربر"
                                value={`${resetResult.user.name || ''} ${resetResult.user.family || ''}`}
                            />
                            <InfoRow label="شماره موبایل" value={resetResult.user.phone_num1} />
                        </div>

                        <button
                            type="button"
                            onClick={() => copyText(buildPasswordMessage())}
                            style={copyButtonStyle}
                        >
                            <FaCopy />
                            کپی متن ارسال به کاربر
                        </button>
                    </div>
                )}

                <div style={listCardStyle}>
                    <div style={listHeaderStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaUsers color="#4dabf7" />
                            <strong>لیست کاربران</strong>
                        </div>

                        <span style={{ color: '#bdc3c7', fontSize: '12px' }}>
                            {loading ? 'در حال بارگذاری...' : `${users.length} کاربر`}
                        </span>
                    </div>

                    <div style={tableWrapperStyle}>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>کاربر</th>
                                    <th style={thStyle}>نام کاربری</th>
                                    <th style={thStyle}>موبایل</th>
                                    <th style={thStyle}>دستگاه‌ها</th>
                                    <th style={thStyle}>وضعیت</th>
                                    <th style={thStyle}>عملیات</th>
                                </tr>
                            </thead>

                            <tbody>
                                {users.length === 0 && !loading ? (
                                    <tr>
                                        <td colSpan="6" style={emptyTableStyle}>
                                            کاربری برای نمایش وجود ندارد.
                                        </td>
                                    </tr>
                                ) : (
                                    users.map(user => {
                                        const active = Number(user.is_active) === 1;
                                        const isBusy = actionLoadingId === user.id;

                                        return (
                                            <tr key={user.id}>
                                            <td style={tdStyle}>
                                            <div style={{ fontWeight: 'bold' }}>
                                             {(user.name || '')} {(user.family || '')}
                                            </div>
                                            </td>

                                                <td style={tdStyle}>
                                                    {user.username}
                                                </td>

                                                <td style={tdStyle}>
                                                    <div>{user.phone_num1}</div>
                                                    {user.phone_num2 && (
                                                        <div style={{ color: '#95a5a6', fontSize: '12px', marginTop: '4px' }}>
                                                            {user.phone_num2}
                                                        </div>
                                                    )}
                                                </td>

                                                <td style={tdStyle}>
                                                    <div style={deviceBadgeStyle}>
                                                        <FaMicrochip />
                                                        {user.devices_count || 0}
                                                    </div>
                                                </td>

                                                <td style={tdStyle}>
                                                    <span style={active ? activeBadgeStyle : inactiveBadgeStyle}>
                                                        {active ? 'فعال' : 'غیرفعال'}
                                                    </span>
                                                </td>

                                                <td style={tdStyle}>
                                                    <div style={actionsStyle}>
                                                        <button
                                                            type="button"
                                                            onClick={() => openEditModal(user)}
                                                            disabled={isBusy}
                                                            style={editButtonStyle}
                                                            title="ویرایش اطلاعات کاربر"
                                                        >
                                                            <FaEdit />
                                                            ویرایش
                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={() => toggleUser(user)}
                                                            disabled={isBusy}
                                                            style={active ? deactivateButtonStyle : activateButtonStyle}
                                                            title={active ? 'غیرفعال کردن' : 'فعال کردن'}
                                                        >
                                                            {active ? <FaToggleOn /> : <FaToggleOff />}
                                                            {active ? 'غیرفعال' : 'فعال'}
                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={() => resetPassword(user)}
                                                            disabled={isBusy || !active}
                                                            style={{
                                                                ...resetButtonStyle,
                                                                opacity: !active ? 0.45 : 1,
                                                                cursor: !active ? 'not-allowed' : 'pointer'
                                                            }}
                                                            title="ریست رمز عبور"
                                                        >
                                                            <FaKey />
                                                            ریست رمز
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {editUser && (
                <div style={modalOverlayStyle}>
                    <form onSubmit={submitEditUser} style={modalStyle}>
                        <div style={modalHeaderStyle}>
                            <strong>ویرایش اطلاعات کاربر</strong>

                            <button
                                type="button"
                                onClick={closeEditModal}
                                style={smallCloseButtonStyle}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div style={modalBodyStyle}>
                            <EditInput
                                label="نام کاربری"
                                value={editForm.username}
                                onChange={(v) => setEditForm(prev => ({ ...prev, username: v }))}
                            />

                            <EditInput
                                label="نام"
                                value={editForm.name}
                                onChange={(v) => setEditForm(prev => ({ ...prev, name: v }))}
                            />

                            <EditInput
                                label="نام خانوادگی"
                                value={editForm.family}
                                onChange={(v) => setEditForm(prev => ({ ...prev, family: v }))}
                            />

                            <EditInput
                                label="شماره موبایل اصلی"
                                value={editForm.phone_num1}
                                onChange={(v) => setEditForm(prev => ({ ...prev, phone_num1: v }))}
                            />

                            <EditInput
                                label="شماره موبایل دوم"
                                value={editForm.phone_num2}
                                onChange={(v) => setEditForm(prev => ({ ...prev, phone_num2: v }))}
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={editLoading}
                            style={saveEditButtonStyle}
                        >
                            <FaSave />
                            {editLoading ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

const InfoRow = ({ label, value }) => (
    <div style={infoRowStyle}>
        <span style={{ color: '#95a5a6' }}>{label}:</span>
        <strong>{value || '-'}</strong>
    </div>
);

const EditInput = ({ label, value, onChange }) => (
    <div>
        <label style={modalLabelStyle}>{label}</label>
        <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={modalInputStyle}
        />
    </div>
);

const topBarStyle = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: '12px',
    marginTop: '24px',
    direction: 'rtl'
};

const searchWrapperStyle = {
    position: 'relative'
};

const searchInputStyle = {
    width: '100%',
    background: 'rgba(16, 24, 32, 0.92)',
    color: '#fff',
    border: '1px solid rgba(52, 152, 219, 0.35)',
    borderRadius: '12px',
    padding: '12px 40px 12px 42px',
    outline: 'none',
    boxSizing: 'border-box'
};

const searchIconInlineStyle = {
    position: 'absolute',
    right: '13px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#4dabf7'
};

const clearSearchButtonStyle = {
    position: 'absolute',
    left: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '26px',
    height: '26px',
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(231,76,60,0.18)',
    color: '#ffb3b3',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer'
};

const refreshButtonStyle = {
    background: '#3498db',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    padding: '0 18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontWeight: 'bold',
    minHeight: '44px'
};

const errorStyle = {
    background: 'rgba(231, 76, 60, 0.15)',
    border: '1px solid rgba(231, 76, 60, 0.4)',
    color: '#ffb3b3',
    borderRadius: '10px',
    padding: '11px',
    marginTop: '14px',
    fontSize: '13px',
    direction: 'rtl'
};

const resetResultStyle = {
    background: 'rgba(31, 45, 58, 0.95)',
    border: '1px solid rgba(46,204,113,0.38)',
    borderRadius: '16px',
    padding: '16px',
    marginTop: '16px',
    color: '#fff',
    direction: 'rtl'
};

const resetHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: '#2ecc71',
    marginBottom: '12px'
};

const resetInfoGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(200px, 1fr))',
    gap: '10px',
    marginBottom: '12px'
};

const smallCloseButtonStyle = {
    border: 'none',
    background: 'rgba(255,255,255,0.06)',
    color: '#fff',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};

const copyButtonStyle = {
    width: '100%',
    background: '#3498db',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '11px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontWeight: 'bold'
};

const listCardStyle = {
    background: 'rgba(31, 45, 58, 0.92)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '16px',
    padding: '16px',
    color: '#fff',
    marginTop: '18px',
    boxShadow: '0 10px 35px rgba(0,0,0,0.25)',
    direction: 'rtl'
};

const listHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '14px'
};

const tableWrapperStyle = {
    overflowX: 'auto'
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '980px',
    fontSize: '13px'
};

const thStyle = {
    textAlign: 'right',
    color: '#f1c40f',
    background: '#101820',
    padding: '12px 10px',
    borderBottom: '1px solid rgba(255,255,255,0.12)',
    position: 'sticky',
    top: 0
};

const tdStyle = {
    padding: '11px 10px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    color: '#ecf0f1',
    verticalAlign: 'middle'
};

const emptyTableStyle = {
    padding: '30px',
    textAlign: 'center',
    color: '#95a5a6'
};

const deviceBadgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: 'rgba(52,152,219,0.14)',
    color: '#4dabf7',
    border: '1px solid rgba(52,152,219,0.28)',
    borderRadius: '999px',
    padding: '5px 10px'
};

const activeBadgeStyle = {
    background: 'rgba(46,204,113,0.14)',
    color: '#2ecc71',
    border: '1px solid rgba(46,204,113,0.32)',
    borderRadius: '999px',
    padding: '5px 10px',
    display: 'inline-block'
};

const inactiveBadgeStyle = {
    background: 'rgba(231,76,60,0.14)',
    color: '#ffb3b3',
    border: '1px solid rgba(231,76,60,0.32)',
    borderRadius: '999px',
    padding: '5px 10px',
    display: 'inline-block'
};

const actionsStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
};

const editButtonStyle = {
    background: '#3498db',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 10px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
};

const activateButtonStyle = {
    background: '#2ecc71',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 10px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
};

const deactivateButtonStyle = {
    background: '#e67e22',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 10px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
};

const resetButtonStyle = {
    background: '#34495e',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 10px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
};

const infoRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    background: '#101820',
    borderRadius: '8px',
    padding: '9px 10px',
    fontSize: '13px'
};

const modalOverlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.65)',
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
};

const modalStyle = {
    width: 'min(620px, 95vw)',
    background: '#1f2d3a',
    color: '#fff',
    borderRadius: '16px',
    border: '1px solid rgba(255,255,255,0.1)',
    boxShadow: '0 10px 40px rgba(0,0,0,0.45)',
    padding: '0',
    direction: 'rtl',
    overflow: 'hidden'
};

const modalHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: '#17212b',
    padding: '14px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    color: '#f1c40f'
};

const modalBodyStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(220px, 1fr))',
    gap: '14px',
    padding: '16px'
};

const modalLabelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '13px',
    color: '#bdc3c7'
};

const modalInputStyle = {
    width: '100%',
    background: '#101820',
    color: '#fff',
    border: '1px solid #34495e',
    borderRadius: '8px',
    padding: '10px',
    outline: 'none',
    boxSizing: 'border-box'
};

const saveEditButtonStyle = {
    margin: '0 16px 16px',
    width: 'calc(100% - 32px)',
    background: '#2ecc71',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontWeight: 'bold'
};

export default AdminUsers;